/**
 * src/services/agentCountryPolicyService.ts
 *
 * Client bindings for the per-country **AI-agent presence policy**.
 *
 * Storage:
 *   - Backend mirror (no on-chain contract yet — see
 *     server/services/agentCountryPolicyMirror.js for the rationale).
 *   - GET  /api/admin/agent-country-policy/:country  — public snapshot.
 *   - POST /api/admin/agent-country-policy           — admin write.
 *
 * Tri-state policy values mirror the WebRTC policy shape so the
 * admin UI can reuse the selector idiom:
 *   0 = NotSet     — no decision recorded; agents default-allowed.
 *   1 = Allowed    — explicit opt-in; agents may operate.
 *   2 = Disallowed — country admin denied AI-agent activity. The
 *                    server rejects /api/v2/agents/:address/position
 *                    writes for this country with HTTP 403 +
 *                    `code: agent_country_disallowed`, and the
 *                    /movement/by-country listing returns an empty
 *                    array so existing markers fade out for new
 *                    viewers.
 */

export const AGENT_COUNTRY_POLICY = {
  NOT_SET:    0,
  ALLOWED:    1,
  DISALLOWED: 2,
} as const;

export type AgentCountryPolicyValue =
  typeof AGENT_COUNTRY_POLICY[keyof typeof AGENT_COUNTRY_POLICY];

export interface AgentCountryPolicyConfig {
  policy: AgentCountryPolicyValue;
  reason: string | null;
  policyVersion: number;
  updatedAt: string | null;
  updatedBy: string | null;
  source?: 'mirror' | 'unset' | string;
}

const DEFAULT_BACKEND = (
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_BACKEND_URL) || ''
) as string;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${DEFAULT_BACKEND}${path}`, {
    ...(init || {}),
    headers: {
      'Content-Type': 'application/json',
      ...((init?.headers as Record<string, string>) || {}),
    },
  });
  const data = await resp.json().catch(() => ({} as any));
  if (!resp.ok || data.ok === false) {
    // Surface the backend's structured error verbatim when present so
    // the admin UI can render "wallet GXXX is not the country admin
    // for country YYY" instead of a generic "save_failed".
    const detail = data?.details ? ` (${JSON.stringify(data.details)})` : '';
    throw new Error((data?.error || `request_failed_${resp.status}`) + detail);
  }
  return data as T;
}

export async function getAgentCountryPolicy(country: number): Promise<AgentCountryPolicyConfig> {
  try {
    const data = await request<any>(`/api/admin/agent-country-policy/${country}`);
    const policy = Number(data.policy ?? 0);
    return {
      policy: (policy === 1 || policy === 2 ? policy : 0) as AgentCountryPolicyValue,
      reason: typeof data.reason === 'string' ? data.reason : null,
      policyVersion: Number(data.policyVersion) || 0,
      updatedAt: data.updatedAt || null,
      updatedBy: data.updatedBy || null,
      source: data.source,
    };
  } catch {
    return {
      policy: AGENT_COUNTRY_POLICY.NOT_SET,
      reason: null,
      policyVersion: 0,
      updatedAt: null,
      updatedBy: null,
      source: 'unset',
    };
  }
}

export interface SetAgentCountryPolicyInput {
  walletAddress?: string;
  policy: AgentCountryPolicyValue;
  reason?: string | null;
  policyVersion?: number;
}

export async function setAgentCountryPolicy(
  country: number,
  input: SetAgentCountryPolicyInput,
): Promise<AgentCountryPolicyConfig> {
  const data = await request<any>(`/api/admin/agent-country-policy`, {
    method: 'POST',
    body: JSON.stringify({ country, ...input }),
  });
  return {
    policy: (Number(data.policy) === 1 || Number(data.policy) === 2
      ? Number(data.policy)
      : 0) as AgentCountryPolicyValue,
    reason: typeof data.reason === 'string' ? data.reason : null,
    policyVersion: Number(data.policyVersion) || 0,
    updatedAt: data.updatedAt || null,
    updatedBy: data.updatedBy || null,
    source: data.source,
  };
}

export function policyLabel(value: AgentCountryPolicyValue): string {
  switch (value) {
    case AGENT_COUNTRY_POLICY.ALLOWED:    return 'Allowed';
    case AGENT_COUNTRY_POLICY.DISALLOWED: return 'Disallowed';
    default:                              return 'Not set (default = allowed)';
  }
}
